// services/analytics.service.ts
import { Injectable, Injector } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { environment } from 'src/environments/environment';
import { ThemeService } from '../client/theme/theme.service';
import { AccountService } from '../user/account.service';
import {
  ANALYTICS_EVENT_CATALOG,
  REQUIRED_FUNNEL_PROPERTIES,
} from './event-catalog';
import { getAffiliateAnalyticsDimensions } from '../shared/travelverse-sso.util';

declare let gtag: Function;

type AnalyticsProperties = Record<string, any>;

interface UnifiedTrackOptions {
  correlationId?: string;
  includeDefaultContext?: boolean;
}

const LEGACY_EVENT_ALIASES: Record<string, string> = {
  // Generic payment outcome aliases.
  // NOTE: `payment_success`, `payment_failed`, and `payment_cancelled` are
  // DISTINCT events from booking_completed/failed / purchase_completed/failed:
  //   - payment_success (step 6): Stripe returned Completed. Backend booking
  //     confirmation has not happened yet.
  //   - payment_failed: in-flight Stripe failure, retryable.
  //   - payment_cancelled: user closed the sheet without entering a card.
  // They are intentionally NOT aliased here.
  payment_succeeded: 'payment_success',
  // Checkout form aliases
  checkout_user_details: 'checkout_user_details_submitted',
  checkout_user_details_submitted: 'checkout_user_details_submitted',
  // Legacy flight card naming
  clicked_product_details: 'item_selected',
  flight_card_details_clicked: 'item_selected',
  // Filter/sort aliases
  filter_applied: 'results_filter_changed',
  results_filter_applied: 'results_filter_changed',
  filter_reset_clicked: 'results_filter_changed',
  results_filter_reset_clicked: 'results_filter_changed',
  sort_applied: 'results_sort_changed',
};

// Default canonical 7-step funnel terminal overrides (hotel, bus, lounge,
// fasttrack, tour, deals, esim, dutyfree, etc.):
// 1 search → 2 results → 3 details → 4 review → 5 payment → 6 booking
// → 7 confirmation (terminal: completed/failed).
// Note: payment_success / payment_failed / payment_cancelled are non-funnel
// sub-events that describe outcomes within step 5. Similarly, booking_submitted
// is a non-funnel sub-event of step 6 indicating async-processing ACK.
const TERMINAL_EVENT_OVERRIDES: Record<
  string,
  { step: string; step_index: number; outcome: string }
> = {
  booking_completed: { step: 'confirmation', step_index: 7, outcome: 'success' },
  purchase_completed: {
    step: 'confirmation',
    step_index: 7,
    outcome: 'success',
  },
  booking_failed: { step: 'confirmation', step_index: 7, outcome: 'error' },
  purchase_failed: { step: 'confirmation', step_index: 7, outcome: 'error' },
};

// Flight has an extra step (prebook = create_booking + price validation)
// between checkout_started and payment. 8-step funnel:
// 1 search → 2 results → 3 details → 4 review → 5 prebook → 6 payment
// → 7 booking → 8 confirmation.
const FLIGHT_TERMINAL_OVERRIDES: Record<
  string,
  { step: string; step_index: number; outcome: string }
> = {
  booking_completed: { step: 'confirmation', step_index: 8, outcome: 'success' },
  booking_failed: { step: 'confirmation', step_index: 8, outcome: 'error' },
  purchase_completed: { step: 'confirmation', step_index: 8, outcome: 'success' },
  purchase_failed: { step: 'confirmation', step_index: 8, outcome: 'error' },
};

const FLIGHT_FLOWS_WITH_PREBOOK = new Set([
  'flight',
  'flexicancel',
  'flexi_cancel_flight',
  'international_flight',
  'domestic_flight',
]);

function getTerminalOverride(
  eventName: string,
  flow: string
): { step: string; step_index: number; outcome: string } | undefined {
  if (AIRPORT_SERVICE_FLOWS.has(flow) && AIRPORT_SERVICE_TERMINAL_OVERRIDES[eventName]) {
    return AIRPORT_SERVICE_TERMINAL_OVERRIDES[eventName];
  }
  if (FLIGHT_FLOWS_WITH_PREBOOK.has(flow) && FLIGHT_TERMINAL_OVERRIDES[eventName]) {
    return FLIGHT_TERMINAL_OVERRIDES[eventName];
  }
  return TERMINAL_EVENT_OVERRIDES[eventName];
}

// Default step + step_index for non-terminal funnel events. Callsites pass
// `flow` + `outcome`; the analytics service fills in step/step_index from
// these defaults so callers don't have to (and can't get them wrong).
// Flight flows override numbering for events that shift due to the prebook step.
const DEFAULT_FUNNEL_STEPS: Record<string, { step: string; step_index: number }> = {
  search_started: { step: 'search', step_index: 1 },
  results_loaded: { step: 'results', step_index: 2 },
  item_selected: { step: 'details', step_index: 3 },
  checkout_started: { step: 'review', step_index: 4 },
  payment_attempted: { step: 'payment', step_index: 5 },
  booking_initiated: { step: 'booking', step_index: 6 },
  purchase_initiated: { step: 'booking', step_index: 6 },
  request_submitted: { step: 'submit', step_index: 5 },
};

const FLIGHT_FUNNEL_STEPS: Record<string, { step: string; step_index: number }> = {
  search_started: { step: 'search', step_index: 1 },
  results_loaded: { step: 'results', step_index: 2 },
  item_selected: { step: 'details', step_index: 3 },
  checkout_started: { step: 'review', step_index: 4 },
  prebook_initiated: { step: 'prebook', step_index: 5 },
  prebook_validated: { step: 'prebook', step_index: 5 },
  prebook_failed: { step: 'prebook', step_index: 5 },
  payment_attempted: { step: 'payment', step_index: 6 },
  booking_initiated: { step: 'booking', step_index: 7 },
};

// Dutyfree (both `dutyfree` and `airport_dutyfree`) is a 7-step funnel that
// ends with request_submitted instead of booking_completed/failed because the
// terminal moment is "order request submitted to fulfillment backend":
// 1 search → 2 results → 3 details → 4 review → 5 payment → 6 booking_initiated
// → 7 confirmation (request_submitted / booking_failed).
const DUTYFREE_FUNNEL_STEPS: Record<string, { step: string; step_index: number }> = {
  ...DEFAULT_FUNNEL_STEPS,
  request_submitted: { step: 'confirmation', step_index: 7 },
};

const DUTYFREE_FLOWS = new Set(['dutyfree', 'airport_dutyfree']);

// Airport service flows (porter, meet & greet) have two terminal paths:
// - With payment:     1 search → 2 results → 3 details → 4 payment_attempted
//                    → 5 booking_initiated → 6 booking_completed (terminal)
// - Without payment:  1 search → 2 results → 3 details → 5 request_submitted (terminal)
const AIRPORT_SERVICE_FUNNEL_STEPS: Record<string, { step: string; step_index: number }> = {
  search_started: { step: 'search', step_index: 1 },
  results_loaded: { step: 'results', step_index: 2 },
  item_selected: { step: 'details', step_index: 3 },
  payment_attempted: { step: 'payment', step_index: 4 },
  booking_initiated: { step: 'booking', step_index: 5 },
  request_submitted: { step: 'confirmation', step_index: 5 },
};

// booking_completed / booking_failed for airport service flows sit at step 6
// (one fewer than the generic 7-step funnel — there is no separate review step
// for porter and meet & greet).
const AIRPORT_SERVICE_TERMINAL_OVERRIDES: Record<
  string,
  { step: string; step_index: number; outcome: string }
> = {
  booking_completed: { step: 'confirmation', step_index: 6, outcome: 'success' },
  booking_failed: { step: 'confirmation', step_index: 6, outcome: 'error' },
};

const AIRPORT_SERVICE_FLOWS = new Set([
  'airport_porter',
  'airport_meet_greet',
]);

function getFunnelStepDefault(
  eventName: string,
  flow: string
): { step: string; step_index: number } | undefined {
  if (FLIGHT_FLOWS_WITH_PREBOOK.has(flow) && FLIGHT_FUNNEL_STEPS[eventName]) {
    return FLIGHT_FUNNEL_STEPS[eventName];
  }
  if (DUTYFREE_FLOWS.has(flow) && DUTYFREE_FUNNEL_STEPS[eventName]) {
    return DUTYFREE_FUNNEL_STEPS[eventName];
  }
  if (AIRPORT_SERVICE_FLOWS.has(flow) && AIRPORT_SERVICE_FUNNEL_STEPS[eventName]) {
    return AIRPORT_SERVICE_FUNNEL_STEPS[eventName];
  }
  return DEFAULT_FUNNEL_STEPS[eventName];
}

const KNOWN_FLIGHT_FLOWS = new Set([
  'flight',
  'flexi_cancel_flight',
  'international_flight',
  'domestic_flight',
]);

// Canonical outcome values. Any other string is normalized via OUTCOME_ALIASES
// or coerced to 'na' with a dev warning.
const CANONICAL_OUTCOMES = new Set(['success', 'error', 'abandon', 'na']);

const OUTCOME_ALIASES: Record<string, string> = {
  // 'attempt' is widely misused as "user clicked the action" — but the event
  // itself signals the click. The action click succeeded; failures get their
  // own dedicated emit. Coerce to success.
  attempt: 'success',
  attempted: 'success',
  failure: 'error',
  failed: 'error',
  retry: 'na',
  initiated: 'success',
};

// URL path → flow mapper. Used to thread page_viewed events into the correct
// per-flow correlation_id bucket so a hotel page view shows up in the hotel
// journey, not in the 'default' bucket.
//
// Match order matters — longer/more specific prefixes first.
const URL_FLOW_PATTERNS: { prefix: string; flow: string }[] = [
  { prefix: '/hotel-deals/', flow: 'hotel_deals' },
  { prefix: '/tours-and-experiences/', flow: 'tours_and_experiences' },
  { prefix: '/tour-and-experience/', flow: 'tours_and_experiences' },
  { prefix: '/hotel/', flow: 'hotel' },
  { prefix: '/flight/', flow: 'flight' },
  { prefix: '/flights/', flow: 'flight' },
  { prefix: '/bus/', flow: 'bus' },
  { prefix: '/lounge/', flow: 'lounge' },
  { prefix: '/lounges/', flow: 'lounge' },
  { prefix: '/fasttrack/', flow: 'fasttrack' },
  { prefix: '/esim/', flow: 'esim' },
  { prefix: '/dutyfreeV2/', flow: 'dutyfree' },
  { prefix: '/dutyfree/', flow: 'dutyfree' },
  { prefix: '/airport/dutyfree-', flow: 'airport_dutyfree' },
  { prefix: '/airport/products-', flow: 'airport_dutyfree' },
  { prefix: '/airport/product-', flow: 'airport_dutyfree' },
  { prefix: '/airport/retail-', flow: 'airport_dutyfree' },
  { prefix: '/airport/lounge', flow: 'airport_lounge' },
  { prefix: '/airport/flights', flow: 'airport_flights' },
  { prefix: '/airport/meet-greet', flow: 'airport_meet_greet' },
  { prefix: '/airport/porter', flow: 'airport_porter' },
  { prefix: '/airport/parking', flow: 'airport_parking' },
  { prefix: '/airport/baggage-insurance', flow: 'airport_baggage_insurance' },
  { prefix: '/airport/airport-map', flow: 'airport_map' },
  { prefix: '/airport/airport-companion', flow: 'airport_companion' },
  { prefix: '/airport/cabs', flow: 'airport_cabs' },
  { prefix: '/airport/', flow: 'airport' },
  { prefix: '/shopping/', flow: 'shopping' },
  { prefix: '/account/', flow: 'account' },
  { prefix: '/authentication/', flow: 'auth' },
  { prefix: '/booking-history/', flow: 'booking_history' },
  { prefix: '/membership', flow: 'membership' },
  { prefix: '/order-history/', flow: 'order_history' },
  { prefix: '/order-details/', flow: 'order_details' },
  { prefix: '/history/', flow: 'history' },
  { prefix: '/activate-membership/', flow: 'activate_membership' },
  { prefix: '/service-list/', flow: 'service_list' },
  { prefix: '/service-details/', flow: 'service_details' },
  { prefix: '/service-request-details/', flow: 'service_request_details' },
  { prefix: '/activities/', flow: 'activities' },
  { prefix: '/travel-home', flow: 'travel_home' },
  { prefix: '/travel-tabs/', flow: 'travel_tabs' },
  { prefix: '/custom-home', flow: 'custom_home' },
  { prefix: '/tab-home', flow: 'tab_home' },
  { prefix: '/home', flow: 'home' },
];

function inferFlowFromUrl(url: string): string | null {
  if (!url) return null;
  // Normalize: strip query/fragment, ensure leading slash.
  const path = url.split('?')[0].split('#')[0];
  const normalized = path.startsWith('/') ? path : `/${path}`;
  for (const { prefix, flow } of URL_FLOW_PATTERNS) {
    if (normalized.startsWith(prefix)) return flow;
  }
  return null;
}

function pathWithoutMatrix(pathname: string): string {
  return pathname.split(';')[0] || pathname;
}

function toSnakeCase(value: string): string {
  if (!value) return '';
  return decodeURIComponent(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private mixpanel: any;
  private readonly sessionStorageKey = 'analytics_session_id';
  private readonly correlationStoragePrefix = 'analytics_correlation_id_';
  private readonly terminalEventStoragePrefix = 'analytics_terminal_event_';
  private readonly funnelStepStoragePrefix = 'analytics_current_funnel_step_';

  constructor(
    private themeService: ThemeService,
    private injector: Injector
  ) {}

  // Lazy-resolve AccountService so the analytics service stays usable even
  // when AccountService hasn't been initialized yet (e.g., during early boot).
  private get accountService(): AccountService | null {
    try {
      return this.injector.get(AccountService, null);
    } catch {
      return null;
    }
  }

  // Affiliate dimensions for the active landing session (sessionStorage + 24h cap), attached
  // to every event while the session is alive; auto-empty once retired.
  private getAffiliateDimensions(): Record<string, string> {
    return getAffiliateAnalyticsDimensions();
  }

  private getUserType(): string {
    const account = this.accountService;
    if (!account) return 'unknown';
    const raw = account.userType;
    if (raw) return toSnakeCase(raw);
    return account.isLoggedIn() ? 'logged_in' : 'guest';
  }

  private isLegacyWrapperEnabled(): boolean {
    return !!environment.enableLegacyAnalyticsWrappers;
  }

  private canSendGa(): boolean {
    if (!this.googleAnalyticsId) return false;
    return (
      typeof window !== 'undefined' &&
      typeof (window as any)['gtag'] === 'function'
    );
  }

  init() {
    this.initGoogleAnalytics();
    this.initMixpanelAnalytics();
  }

  get googleAnalyticsId() {
    const server = environment.production ? 'prod' : 'dev';
    return this.themeService.getAnalyticsConfig?.google_analytics?.[server]
      ?.measurementIdGA;
  }

  get getMixpanelId() {
    const server = environment.production ? 'prod' : 'dev';
    return this.themeService.getAnalyticsConfig?.mixpanel_analytics?.[server]
      ?.mixpanelToken;
  }
  private initGoogleAnalytics() {
    const googleAnalyticsTrackingId = this.googleAnalyticsId;
    if (!googleAnalyticsTrackingId) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsTrackingId}`;
    document.head.appendChild(script);

    window['dataLayer'] = window['dataLayer'] || [];
    if (typeof window['gtag'] !== 'function') {
      window['gtag'] = function gtag() {
        window['dataLayer'].push(arguments);
      };
    }
    window['gtag']('js', new Date());
    window['gtag']('config', googleAnalyticsTrackingId, {
      send_page_view: false,
    });
  }

  private initMixpanelAnalytics() {
    const mixpanelTrackingId = this.getMixpanelId;
    this.mixpanel = mixpanel;
    if (!mixpanelTrackingId) return;
    this.mixpanel.init(mixpanelTrackingId, {
      debug: !environment.production,
      track_pageview: false, // We'll handle this manually
      persistence: 'localStorage',
      ip: false, // For GDPR compliance
      api_host: 'https://api.mixpanel.com',
      record_sessions_percent: 5, //records 1% of all sessions
    });
  }

  // Identify user
  identify(userId: string, userProperties?: any): void {
    this.mixpanel.identify(userId);
    if (userProperties) {
      this.mixpanel.people.set(userProperties);
    }
  }

  // Set user properties
  setUserProperties(properties: any): void {
    this.mixpanel.people.set(properties);
  }

  private getPlatform(): string {
    const userAgent = navigator?.userAgent?.toLowerCase() || '';
    if (userAgent.includes('android')) return 'android';
    if (userAgent.includes('iphone') || userAgent.includes('ipad'))
      return 'ios';
    return 'web';
  }

  private generateRandomId(): string {
    return (
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).replace('0.', '').slice(0, 10)
    );
  }

  private getSessionId(): string {
    try {
      const existingSessionId = sessionStorage.getItem(this.sessionStorageKey);
      if (existingSessionId) return existingSessionId;
      const newSessionId = this.generateRandomId();
      sessionStorage.setItem(this.sessionStorageKey, newSessionId);
      return newSessionId;
    } catch {
      return this.generateRandomId();
    }
  }

  getOrCreateCorrelationId(flow: string): string {
    const safeFlow = toSnakeCase(flow || 'default');
    const storageKey = `${this.correlationStoragePrefix}${safeFlow}`;
    try {
      const existingCorrelationId = sessionStorage.getItem(storageKey);
      if (existingCorrelationId) return existingCorrelationId;
      const newCorrelationId = this.generateRandomId();
      sessionStorage.setItem(storageKey, newCorrelationId);
      return newCorrelationId;
    } catch {
      return this.generateRandomId();
    }
  }

  clearCorrelationId(flow: string): void {
    const safeFlow = toSnakeCase(flow || 'default');
    const storageKey = `${this.correlationStoragePrefix}${safeFlow}`;
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // no-op
    }
  }

  /**
   * Starts a new journey instance for a flow by forcing a fresh correlation_id.
   * Use this at funnel step 1 (e.g., search_started).
   */
  startJourney(flow: string): string {
    this.clearCorrelationId(flow);
    this.clearCurrentFunnelStep(flow);
    return this.getOrCreateCorrelationId(flow);
  }

  /**
   * Ends a journey instance for a flow. Use this after terminal steps
   * (e.g., booking_completed/booking_failed) so the next attempt is a new journey.
   */
  endJourney(flow: string): void {
    this.clearCorrelationId(flow);
    this.clearCurrentFunnelStep(flow);
  }

  /**
   * Record the most recent canonical funnel step for a flow so subsequent
   * non-funnel events can carry it as enrichment.
   */
  private recordCurrentFunnelStep(
    flow: string,
    step: string,
    stepIndex: number
  ): void {
    const safeFlow = toSnakeCase(flow || 'default');
    const storageKey = `${this.funnelStepStoragePrefix}${safeFlow}`;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ step, step_index: stepIndex })
      );
    } catch {
      // no-op
    }
  }

  private getCurrentFunnelStep(
    flow: string
  ): { step: string; step_index: number } | null {
    const safeFlow = toSnakeCase(flow || 'default');
    const storageKey = `${this.funnelStepStoragePrefix}${safeFlow}`;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private clearCurrentFunnelStep(flow: string): void {
    const safeFlow = toSnakeCase(flow || 'default');
    const storageKey = `${this.funnelStepStoragePrefix}${safeFlow}`;
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // no-op
    }
  }

  private isFunnelCriticalEvent(eventName: string): boolean {
    return !!ANALYTICS_EVENT_CATALOG[eventName]?.funnelCritical;
  }

  /**
   * Coerce outcome values to the canonical set (success | error | abandon | na).
   * Known legacy aliases are mapped; unknown strings are warned about and
   * coerced to 'na' so dashboards don't get polluted with one-off values.
   */
  private normalizeOutcome(rawOutcome: string, eventName: string): string {
    const lower = rawOutcome.trim().toLowerCase();
    if (CANONICAL_OUTCOMES.has(lower)) return lower;
    if (OUTCOME_ALIASES[lower]) return OUTCOME_ALIASES[lower];
    if (!environment.production) {
      console.warn(
        `[Analytics] Non-canonical outcome "${rawOutcome}" on event "${eventName}". Coerced to 'na'. Use one of: success | error | abandon | na.`
      );
    }
    return 'na';
  }

  private buildDefaultContext(
    properties?: AnalyticsProperties
  ): AnalyticsProperties {
    const path = this.getCleanPathFromUrl(window.location.href);
    const nowIso = new Date().toISOString();
    const flowFromProps =
      properties?.flow || properties?.flow_type || 'default';
    const flow = toSnakeCase(flowFromProps);

    return {
      client_id: this.themeService.getClientName || 'unknown',
      partner_id: this.themeService.getAppPid || 'unknown',
      platform: this.getPlatform(),
      page_path: path,
      route_name: path,
      session_id: this.getSessionId(),
      correlation_id:
        properties?.correlation_id || this.getOrCreateCorrelationId(flow),
      timestamp_iso: nowIso,
      user_type: this.getUserType(),
      ...this.getAffiliateDimensions(),
    };
  }

  private normalizeProperties(
    properties?: AnalyticsProperties
  ): AnalyticsProperties {
    if (!properties) return {};
    return this.convertKeysToSnakeCase(properties);
  }

  private resolveCanonicalEventName(eventName: string): string {
    const normalizedName = toSnakeCase(eventName);
    return LEGACY_EVENT_ALIASES[normalizedName] || normalizedName;
  }

  private shouldSuppressDuplicateTerminalEvent(
    eventName: string,
    payload: AnalyticsProperties
  ): boolean {
    if (!TERMINAL_EVENT_OVERRIDES[eventName]) return false;

    // We only dedup terminal events when a stable identifier is available.
    // For failures without a booking id (e.g. payment gateway aborts), retries
    // are a valuable signal and should pass through.
    const bookingId =
      payload.booking_id ||
      payload.booking_reference_id ||
      payload.order_id ||
      payload.transaction_id;
    if (!bookingId) return false;

    const flow = toSnakeCase(payload.flow || payload.flow_type || 'default');
    const storageKey = `${this.terminalEventStoragePrefix}${eventName}_${flow}_${bookingId}`;
    try {
      if (sessionStorage.getItem(storageKey)) return true;
      sessionStorage.setItem(storageKey, '1');
    } catch {
      // If sessionStorage is unavailable, prefer sending over losing analytics.
    }
    return false;
  }

  private sendToMixpanel(
    eventName: string,
    properties: AnalyticsProperties
  ): void {
    this.mixpanel.track(eventName, properties);
  }

  private sendToGa(eventName: string, properties: AnalyticsProperties): void {
    if (!this.canSendGa()) return;
    const gaEventName = toSnakeCase(eventName);
    (window as any)['gtag']('event', gaEventName, properties);
  }

  private validateUnifiedEvent(
    eventName: string,
    payload: AnalyticsProperties
  ): void {
    if (environment.production) return;
    const eventDef = ANALYTICS_EVENT_CATALOG[eventName];
    if (!eventDef) return;
    const requiredProps = eventDef.requiredProperties || [];
    const missingProps = requiredProps.filter((key) => {
      const value = payload[key];
      return value === undefined || value === null || value === '';
    });
    if (missingProps.length > 0) {
      console.warn(
        `[Analytics Validation] Event "${eventName}" missing required properties: ${missingProps.join(
          ', '
        )}`,
        payload
      );
    }

    if (eventDef.funnelCritical) {
      const missingFunnelProps = REQUIRED_FUNNEL_PROPERTIES.filter((key) => {
        const value = payload[key];
        return value === undefined || value === null || value === '';
      });
      if (missingFunnelProps.length > 0) {
        console.warn(
          `[Analytics Validation] Funnel event "${eventName}" missing funnel properties: ${missingFunnelProps.join(
            ', '
          )}`,
          payload
        );
      }
    }
  }

  trackUnified(
    eventName: string,
    properties?: AnalyticsProperties,
    options?: UnifiedTrackOptions
  ): void {
    const canonicalEventName = this.resolveCanonicalEventName(eventName);
    const includeDefaultContext = options?.includeDefaultContext !== false;
    const normalizedProps = this.normalizeProperties(properties);
    const flowValue = String(
      normalizedProps.flow || normalizedProps.flow_type || ''
    );

    if (
      includeDefaultContext &&
      canonicalEventName === 'search_started' &&
      flowValue &&
      !normalizedProps.correlation_id
    ) {
      this.startJourney(flowValue);
    }

    const baseContext = includeDefaultContext
      ? this.buildDefaultContext(normalizedProps)
      : {};

    const payload: AnalyticsProperties = {
      ...baseContext,
      ...normalizedProps,
    };

    if (
      canonicalEventName !== toSnakeCase(eventName) &&
      !payload.legacy_event_name
    ) {
      payload.legacy_event_name = eventName;
    }

    if (options?.correlationId) {
      payload.correlation_id = options.correlationId;
    }

    // Normalize outcome to the canonical set BEFORE the terminal override so
    // legacy values like 'attempt' / 'failure' get coerced consistently.
    if (payload.outcome !== undefined && payload.outcome !== null) {
      payload.outcome = this.normalizeOutcome(
        String(payload.outcome),
        canonicalEventName
      );
    }

    // Look up terminal step override per flow — flight uses step_index 8
    // (extra prebook step), other flows use 7.
    const terminalOverride = getTerminalOverride(canonicalEventName, flowValue);
    if (terminalOverride) {
      if (
        !environment.production &&
        payload.outcome &&
        payload.outcome !== terminalOverride.outcome
      ) {
        console.warn(
          `[Analytics] Overriding outcome="${payload.outcome}" to "${terminalOverride.outcome}" for "${canonicalEventName}". Use the matching success/failure event instead.`
        );
      }
      payload.step = terminalOverride.step;
      payload.step_index = terminalOverride.step_index;
      payload.outcome = terminalOverride.outcome;
    } else {
      // Non-terminal canonical funnel events get step + step_index filled in
      // from DEFAULT_FUNNEL_STEPS / FLIGHT_FUNNEL_STEPS if the caller didn't
      // pass them. Lets callers stay terse: just emit with { flow, outcome }
      // and any event-specific properties.
      const stepDefault = getFunnelStepDefault(canonicalEventName, flowValue);
      if (stepDefault) {
        if (payload.step === undefined || payload.step === null) {
          payload.step = stepDefault.step;
        }
        if (
          payload.step_index === undefined ||
          payload.step_index === null
        ) {
          payload.step_index = stepDefault.step_index;
        }
      }
    }

    if (
      this.shouldSuppressDuplicateTerminalEvent(canonicalEventName, payload)
    ) {
      return;
    }

    // Funnel-step bookkeeping. The rule is:
    //   - Only canonical funnel-critical events carry step / step_index /
    //     outcome. Those events RECORD their step so the rest of the journey
    //     can read it.
    //   - Non-funnel events have step / step_index / outcome STRIPPED (callers
    //     often passed wrong values, e.g. tagging room_selected as step 4),
    //     and are enriched with current_funnel_step / current_funnel_step_index
    //     from the last recorded canonical step. This separates "what funnel
    //     checkpoint did the user reach" from "what UI event fired."
    const isFunnelEvent = this.isFunnelCriticalEvent(canonicalEventName);
    if (isFunnelEvent) {
      if (flowValue && payload.step && typeof payload.step_index === 'number') {
        this.recordCurrentFunnelStep(
          flowValue,
          String(payload.step),
          payload.step_index
        );
      }
    } else {
      if (
        !environment.production &&
        (payload.step !== undefined || payload.step_index !== undefined)
      ) {
        console.warn(
          `[Analytics] Non-funnel event "${canonicalEventName}" passed step/step_index. These are stripped — only canonical funnel events carry them. Use current_funnel_step (auto-enriched) to know which step the user was on.`
        );
      }
      delete payload.step;
      delete payload.step_index;
      // outcome: keep it. Non-funnel events legitimately have outcomes
      // (e.g., a filter apply can succeed or fail), separate from funnel
      // progression.
      if (flowValue) {
        const recorded = this.getCurrentFunnelStep(flowValue);
        if (recorded) {
          if (payload.current_funnel_step === undefined) {
            payload.current_funnel_step = recorded.step;
          }
          if (payload.current_funnel_step_index === undefined) {
            payload.current_funnel_step_index = recorded.step_index;
          }
        }
      }
    }

    this.validateUnifiedEvent(canonicalEventName, payload);
    const sanitizedPayload = this.sanitizePayload(payload);
    this.sendToMixpanel(canonicalEventName, sanitizedPayload);
    this.sendToGa(canonicalEventName, sanitizedPayload);

    // Only end the journey on success terminals. Keeping correlation_id across
    // failures lets us see "failed → retried → converted" as one journey.
    // request_submitted is a terminal for dutyfree / porter / meet-greet flows.
    if (
      flowValue &&
      (canonicalEventName === 'booking_completed' ||
        canonicalEventName === 'purchase_completed' ||
        (canonicalEventName === 'request_submitted' &&
          payload.outcome === 'success'))
    ) {
      this.endJourney(flowValue);
    }
  }

  private sanitizePayload(payload: AnalyticsProperties): AnalyticsProperties {
    const sanitizedPayload: AnalyticsProperties = { ...payload };
    if ('context' in sanitizedPayload) {
      delete sanitizedPayload.context;
    }
    if ('error' in sanitizedPayload) {
      const errorValue = sanitizedPayload.error;
      if (errorValue && typeof errorValue === 'object') {
        sanitizedPayload.error = {
          message: errorValue.message,
          code: errorValue.code,
        };
      } else {
        sanitizedPayload.error = { message: String(errorValue) };
      }
    }
    return sanitizedPayload;
  }

  //! Should be deleted later once the migration to new analytics implementation is completed
  trackEvent(eventName: string, properties?: any): void {
    if (!this.isLegacyWrapperEnabled()) {
      if (!environment.production) {
        console.warn('[Analytics] Blocked legacy wrapper call: trackEvent');
      }
      return;
    }
    this.trackUnified(eventName, properties);
  }

  convertKeysToSnakeCase(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.convertKeysToSnakeCase(item));
    } else if (obj !== null && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const snakeKey = key
          .replace(/([A-Z])/g, '_$1')
          .replace(/^_/, '')
          .toLowerCase();
        acc[snakeKey] = this.convertKeysToSnakeCase(obj[key]);
        return acc;
      }, {} as any);
    }
    return obj;
  }

  identifyUser(userId: string): void {
    this.mixpanel.identify(userId);
  }

  setProfileParameters(properties: any): void {
    this.mixpanel.people.set(properties);
  }
  //! End: Should be deleted later once the migration to new analytics implementation is completed

  // Track events
  track(eventName: string, properties?: any): void {
    if (!this.isLegacyWrapperEnabled()) {
      if (!environment.production) {
        console.warn('[Analytics] Blocked legacy wrapper call: track');
      }
      return;
    }
    const normalizedProps = this.normalizeProperties(properties);
    const payload: AnalyticsProperties = {
      ...this.buildDefaultContext(normalizedProps),
      ...normalizedProps,
    };
    this.sendToMixpanel(eventName, payload);
  }

  trackGA(eventName: string) {
    if (!this.isLegacyWrapperEnabled()) {
      if (!environment.production) {
        console.warn('[Analytics] Blocked legacy wrapper call: trackGA');
      }
      return;
    }
    this.sendToGa(eventName, {
      event_category: 'event',
      event_label: toSnakeCase(eventName),
      page_path: this.getCleanPathFromUrl(window.location.href),
    });
  }

  getCleanPathFromUrl(url: string): string {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const withoutMatrix = pathWithoutMatrix(pathname) || '/';
      const slug = withoutMatrix.replace(/^\/|\/$/g, '').replace(/\//g, '-');
      return slug || 'page';
    } catch {
      return 'page';
    }
  }

  // Transient routes that we should never emit a page_view for. These are
  // Ionic/Angular navigation intermediaries — the user never actually "lands"
  // on them.
  private readonly PAGE_VIEW_SKIP_PATHS = new Set([
    'blank',
    'redirecting',
    '',
  ]);

  // Defer-merge: the NavigationEnd (global) emit and a per-page lifecycle
  // emit BOTH call trackPageView for the same URL within ~200ms. Instead of
  // firing both or arbitrarily picking one, we collect calls into a pending
  // bucket per URL and emit a single event with merged properties. Later
  // calls override earlier on conflict, so per-page emits (which carry
  // richer context like search_query) win on shared keys.
  private pendingPageView: {
    cleanPath: string;
    resolvedUrl: string;
    pageName: string;
    properties: AnalyticsProperties;
    timer: any;
  } | null = null;
  // 600ms is enough to cover the gap between NavigationEnd (global emit)
  // and ionViewDidEnter (per-page emit) even on slow first-mount cases.
  // Bumped from 250 after observing dual page_views in real-app testing.
  private readonly PAGE_VIEW_FLUSH_MS = 600;

  trackPageView(pageName: string, properties?: any): void {
    const url = properties?.url || window.location.href;
    const resolvedUrl = typeof url === 'string' ? url : window.location.href;
    const cleanPath = this.getCleanPathFromUrl(resolvedUrl);

    if (this.PAGE_VIEW_SKIP_PATHS.has(cleanPath)) {
      // Transient route; the next NavigationEnd will fire the real page_view.
      return;
    }

    const normalizedProps = this.normalizeProperties(properties);

    if (this.pendingPageView && this.pendingPageView.cleanPath !== cleanPath) {
      // Different URL came in before the previous one flushed — flush now
      // so we don't lose it (e.g., rapid navigation).
      this.flushPendingPageView();
    }

    if (this.pendingPageView && this.pendingPageView.cleanPath === cleanPath) {
      // Merge into existing pending. Later calls win on shared keys.
      clearTimeout(this.pendingPageView.timer);
      this.pendingPageView.properties = {
        ...this.pendingPageView.properties,
        ...normalizedProps,
      };
      // Prefer a non-empty pageName from the later (per-page) call if present.
      if (pageName) this.pendingPageView.pageName = pageName;
      this.pendingPageView.resolvedUrl = resolvedUrl;
    } else {
      this.pendingPageView = {
        cleanPath,
        resolvedUrl,
        pageName: pageName || '',
        properties: normalizedProps,
        timer: null,
      };
    }

    this.pendingPageView.timer = setTimeout(
      () => this.flushPendingPageView(),
      this.PAGE_VIEW_FLUSH_MS
    );
  }

  private flushPendingPageView(): void {
    if (!this.pendingPageView) return;
    const { cleanPath, resolvedUrl, pageName, properties, timer } =
      this.pendingPageView;
    clearTimeout(timer);
    this.pendingPageView = null;

    // Derive flow from the URL so page_viewed threads into the per-flow
    // correlation_id bucket. Without this, every page_view defaults to the
    // 'default' bucket and appears disconnected from the journey.
    const inferredFlow = inferFlowFromUrl(resolvedUrl);
    const pageViewPayload: AnalyticsProperties = {
      ...(inferredFlow ? { flow: inferredFlow } : {}),
      ...properties,
      page_name: pageName,
      route_name: cleanPath,
      page_path: cleanPath,
      url: resolvedUrl,
    };

    this.trackUnified('page_viewed', pageViewPayload);

    if (!this.canSendGa()) return;
    (window as any)['gtag']('event', 'page_view', {
      ...pageViewPayload,
      page_title: pageName,
      page_location: resolvedUrl,
    });
  }

  // Reset user (for logout)
  reset(): void {
    this.mixpanel.reset();
  }
  // Track successful login
  trackLogin(userId: string, method: string = 'phone', properties?: any): void {
    this.trackUnified('user_login', {
      login_method: method,
      timestamp: new Date().toISOString(),
    });

    this.identify(userId, properties);
  }

  trackGuestLogin(userId: string, properties?: any): void {
    this.trackUnified('guest_login', {
      login_method: 'hash',
      timestamp: new Date().toISOString(),
    });

    this.identify(userId, properties);
  }

  // Track login failure
  trackLoginFailure(error: string, method: string = 'email'): void {
    this.trackUnified('login_failed', {
      error_message: error,
      login_method: method,
      timestamp: new Date().toISOString(),
    });
  }
  // Track login failure
  trackGuestLoginFailure(error: string, user_type: string = ''): void {
    this.trackUnified('login_failed', {
      error_message: error,
      login_method: 'hash',
      user_type,
      timestamp: new Date().toISOString(),
    });
  }

  // Track registration
  trackRegistration(userId: string, method: string = 'email'): void {
    this.trackUnified('user_registered', {
      registration_method: method,
      timestamp: new Date().toISOString(),
    });

    this.identify(userId, {
      $created: new Date().toISOString(),
      registration_method: method,
    });
  }

  // Track logout
  trackLogout(): void {
    this.trackUnified('user_logout', {
      timestamp: new Date().toISOString(),
    });
    this.reset();
  }

  // View product
  trackProductView(product: any): void {
    this.trackUnified('product_viewed', {
      product_id: product.id,
      product_name: product.name,
      category: product.category,
      price: product.price,
      currency: 'USD',
      brand: product.brand,
      variant: product.variant,
      availability: product.inStock ? 'In Stock' : 'Out of Stock',
    });
  }

  // Add to cart
  trackAddToCart(product: any, quantity: number = 1): void {
    this.trackUnified('product_added_to_cart', {
      product_id: product.id,
      product_name: product.name,
      category: product.category,
      price: product.price,
      quantity: quantity,
      total_value: product.price * quantity,
      currency: 'USD',
    });
  }

  // Remove from cart
  trackRemoveFromCart(product: any, quantity: number = 1): void {
    this.trackUnified('product_removed_from_cart', {
      product_id: product.id,
      product_name: product.name,
      quantity: quantity,
      value_removed: product.price * quantity,
      currency: 'USD',
    });
  }

  // Start checkout
  trackCheckoutStarted(cartItems: any[], totalValue: number): void {
    this.trackUnified('checkout_started', {
      cart_value: totalValue,
      item_count: cartItems.length,
      currency: 'USD',
      products: cartItems.map((item) => ({
        product_id: item.id,
        product_name: item.name,
        quantity: item.quantity,
        price: item.price,
      })),
    });
  }

  // Complete purchase
  trackPurchase(order: any): void {
    this.trackUnified('purchase_completed', {
      order_id: order.id,
      order_value: order.total,
      currency: order.currency || 'USD',
      item_count: order.items.length,
      payment_method: order.paymentMethod,
      shipping_method: order.shippingMethod,
      coupon_code: order.couponCode,
      discount_amount: order.discount,
      tax_amount: order.tax,
      shipping_amount: order.shipping,
      products: order.items.map((item) => ({
        product_id: item.productId,
        product_name: item.name,
        category: item.category,
        quantity: item.quantity,
        price: item.price,
        total: item.quantity * item.price,
      })),
    });

    // Update user profile with purchase data
    this.mixpanel.people.track_charge(order.total, {
      order_id: order.id,
      timestamp: new Date().toISOString(),
    });

    this.setUserProperties({
      last_purchase_date: new Date().toISOString(),
      total_orders: this.mixpanel.people.increment('total_orders', 1),
      lifetime_value: this.mixpanel.people.increment(
        'lifetime_value',
        order.total
      ),
    });
  }
  // View service
  trackServiceView(service: any): void {
    this.trackUnified('service_viewed', {
      service_id: service.id,
      service_name: service.name,
      category: service.category,
      price: service.price,
      duration: service.duration,
      provider: service.provider,
      availability: service.available,
    });
  }

  // Start booking process
  trackBookingStarted(service: any): void {
    this.trackUnified('booking_started', {
      service_id: service.id,
      service_name: service.name,
      service_price: service.price,
      service_category: service.category,
    });
  }

  // Select date/time
  trackBookingDateSelected(service: any, date: string, time: string): void {
    this.trackUnified('booking_date_selected', {
      service_id: service.id,
      service_name: service.name,
      selected_date: date,
      selected_time: time,
      day_of_week: new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
      }),
    });
  }

  // Complete booking
  trackBookingCompleted(booking: any): void {
    this.trackUnified('booking_completed', {
      booking_id: booking.id,
      service_id: booking.serviceId,
      service_name: booking.serviceName,
      booking_date: booking.date,
      booking_time: booking.time,
      total_amount: booking.amount,
      payment_method: booking.paymentMethod,
      provider: booking.provider,
      duration: booking.duration,
    });

    this.setUserProperties({
      last_booking_date: new Date().toISOString(),
      total_bookings: this.mixpanel.people.increment('total_bookings', 1),
    });
  }

  // Cancel booking
  trackBookingCancelled(booking: any, reason?: string): void {
    this.trackUnified('booking_cancelled', {
      booking_id: booking.id,
      service_id: booking.serviceId,
      cancellation_reason: reason,
      days_before_booking: this.getDaysDifference(
        new Date(),
        new Date(booking.date)
      ),
      refund_amount: booking.refundAmount,
    });
  }

  // Helper method to calculate days difference between two dates
  private getDaysDifference(date1: Date, date2: Date): number {
    const diffTime = date2.getTime() - date1.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // View subscription plans
  trackSubscriptionPlansViewed(): void {
    this.trackUnified('subscription_plans_viewed', {
      timestamp: new Date().toISOString(),
    });
  }

  // Start subscription process
  trackSubscriptionStarted(plan: any): void {
    this.trackUnified('subscription_started', {
      plan_id: plan.id,
      plan_name: plan.name,
      plan_price: plan.price,
      billing_cycle: plan.billingCycle,
      features: plan.features,
    });
  }

  // Complete subscription
  trackSubscriptionCompleted(subscription: any): void {
    this.trackUnified('subscription_completed', {
      subscription_id: subscription.id,
      plan_id: subscription.planId,
      plan_name: subscription.planName,
      plan_price: subscription.price,
      billing_cycle: subscription.billingCycle,
      trial_period: subscription.trialDays,
      payment_method: subscription.paymentMethod,
      start_date: subscription.startDate,
      next_billing_date: subscription.nextBillingDate,
    });

    this.setUserProperties({
      subscription_status: 'active',
      subscription_plan: subscription.planName,
      subscription_start_date: subscription.startDate,
      monthly_recurring_revenue:
        subscription.billingCycle === 'monthly'
          ? subscription.price
          : subscription.price / 12,
    });
  }

  // Subscription renewal
  trackSubscriptionRenewed(subscription: any): void {
    this.trackUnified('subscription_renewed', {
      subscription_id: subscription.id,
      plan_name: subscription.planName,
      amount_charged: subscription.price,
      billing_cycle: subscription.billingCycle,
      renewal_date: new Date().toISOString(),
    });
  }

  // Subscription cancelled
  trackSubscriptionCancelled(subscription: any, reason?: string): void {
    this.trackUnified('subscription_cancelled', {
      subscription_id: subscription.id,
      plan_name: subscription.planName,
      cancellation_reason: reason,
      days_subscribed: this.getDaysDifference(
        new Date(subscription.startDate),
        new Date()
      ),
      total_amount_paid: subscription.totalPaid,
    });

    this.setUserProperties({
      subscription_status: 'cancelled',
      cancellation_date: new Date().toISOString(),
      cancellation_reason: reason,
    });
  }
  // Track conversion funnels
  trackFunnelStep(
    funnelName: string,
    step: string,
    stepNumber: number,
    properties?: any
  ): void {
    const normalizedProps = this.normalizeProperties(properties);
    this.trackUnified('funnel_step_viewed', {
      flow: toSnakeCase(funnelName || 'default'),
      step: toSnakeCase(step || 'unknown'),
      step_index: Number(stepNumber) || 0,
      outcome: normalizedProps?.outcome || 'na',
      funnel_name: funnelName,
      ...normalizedProps,
    });
  }

  // Example: E-commerce funnel
  trackEcommerceFunnel(step: string, properties?: any): void {
    const steps = {
      product_view: 1,
      add_to_cart: 2,
      checkout_start: 3,
      payment_info: 4,
      purchase: 5,
    };

    this.trackFunnelStep('E-commerce Purchase', step, steps[step], properties);
  }
  // Track time spent on page
  trackTimeOnPage(pageName: string, timeSpent: number): void {
    const props = {
      page_name: pageName,
      time_spent_seconds: timeSpent,
      engagement_level: this.getEngagementLevel(timeSpent),
    };
    this.trackUnified('time_on_page', props);
    const cleanPath = this.getCleanPathFromUrl(window.location.href);
    if (!this.canSendGa()) return;
    (window as any)['gtag']('event', 'time_on_page', {
      event_category: 'engagement',
      event_label: cleanPath,
      page_path: cleanPath,
      ...props,
    });
  }

  // Track scroll depth
  trackScrollDepth(pageName: string, scrollPercentage: number): void {
    this.trackUnified('scroll_depth', {
      page_name: pageName,
      scroll_percentage: scrollPercentage,
      engagement_level:
        scrollPercentage > 75
          ? 'high'
          : scrollPercentage > 50
          ? 'medium'
          : 'low',
    });
  }

  // Track feature usage
  trackFeatureUsage(featureName: string, context?: any): void {
    this.trackUnified('feature_used', {
      feature_name: featureName,
      context: context,
      timestamp: new Date().toISOString(),
    });
  }

  // Helper method to determine engagement level based on time spent
  private getEngagementLevel(timeSpent: number): string {
    if (timeSpent > 300) {
      return 'high';
    } else if (timeSpent > 120) {
      return 'medium';
    } else {
      return 'low';
    }
  }
  // Track errors
  trackError(error: any, context?: string): void {
    this.trackUnified('error_occurred', {
      error_message: error.message,
      error_stack: error.stack,
      context: context,
      url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }

  // Track performance metrics
  trackPerformance(metric: string, value: number, context?: any): void {
    this.trackUnified('performance_metric', {
      metric_name: metric,
      metric_value: value,
      context: context,
      timestamp: new Date().toISOString(),
    });
  }

  trackSelectedService(service: any): void {
    this.trackUnified('selected_service_details', {
      adultOrigPrice: service?.adultOrigPrice,
      adultPrice: service?.adultPrice,
      airportData: service?.airportData,
      basePrice: service?.basePrice,
      campaignId: service?.campaignId,
      childOrigPrice: service?.childOrigPrice,
      childPrice: service?.childPrice,
      currency: service?.currency,
      discount: service?.discount,
      finalPrice: service?.finalPrice,
      id: service?.id,
      infantOrigPrice: service?.infantOrigPrice,
      infantPrice: service?.infantPrice,
      origPrice: service?.origPrice,
      serviceid: service?.serviceid,
      skuid: service?.skuid,
      source: service?.source,
      tag: service?.tag,
      title: service?.title,
      type: service?.type,
    });
  }

  trackSelectedAirport(airport: any): void {
    this.trackUnified('selected_airport', {
      airport_code: airport?.airportCode,
      airport_name: airport?.airportName,
      city: airport?.city,
      country: airport?.country,
      address: airport?.address,
      latitude: airport?.latitude,
      longitude: airport?.longitude,
      pre_order_enabled: airport?.preOrderEnabled,
    });
  }

  // Dutyfree: Track when a user selects a package/card
  trackDutyfreePackageSelected(details: any, flow: string = 'dutyfree'): void {
    this.trackUnified('item_selected', {
      flow,
      outcome: 'success',
      legacy_event_name: 'selected_package',
      package_title: details?.title,
      package_store_name: details?.storeName,
      package_subcategory: details?.subCategory,
      package_tag: details?.tag,
      package_id: details?.packageId,
      package_name: details?.packageName,
      retailer_id: details?.retailerId,
      retailer_name: details?.retailerName,
      item_type: details?.itemType,
      is_product_cancellable: details?.productCancellable,
      product_status: details?.status,
      airport_code: details?.airportCode,
      location_name: details?.locationName,
      currency: details?.currency,
      original_price: details?.originalPrice,
      final_price: details?.finalPrice,
    });
  }

  trackCheckoutFormSubmission(formValues: any): void {
    this.trackUnified('checkout_user_details_submitted', {
      first_name: formValues?.first_name,
      last: formValues?.last,
      calling_code: formValues?.calling_code,
      phone_number: formValues?.phone_number,
      email: formValues?.email,
      country: formValues?.country,
      country_code: formValues?.country_code,
      flight_no: formValues?.flight_no,
      pickup_date: formValues?.pickup_date,
      pickup_time: formValues?.pickup_time,
      terminal: formValues?.terminal,
    });
  }

  trackUpdatedProductCart(product: any, flow?: string): void {
    const cartDetails = {
      product_title: product?.cartDetails[0]?.title,
      product_id: product?.cartDetails[0]?.id,
      product_category: product?.cartDetails[0]?.category,
      product_currency: product?.cartDetails[0]?.currency,
      totalPrice: product?.cartDetails[0]?.totalPrice,
      txOfferTag: product?.cartDetails[0]?.txOfferTag,
    };
    const discountConfig = {
      productDiscountText: product?.discountConfig?.productDiscountText,
      productDiscountAmount: product?.discountConfig?.productDiscountAmount,
      cartDiscountText: product?.discountConfig?.cartDiscountText,
      cartDiscountAmount: product?.discountConfig?.cartDiscountAmount,
      promotionalDiscountText: product?.discountConfig?.promotionalDiscountText,
      promotionalDiscountAmount:
        product?.discountConfig?.promotionalDiscountAmount,
      saleDiscountText: product?.discountConfig?.saleDiscountText,
      saleDiscountAmount: product?.discountConfig?.saleDiscountAmount,
      totalPrice: product?.discountConfig?.totalPrice,
      totalDiscount: product?.discountConfig?.totalDiscount,
      cartMaxDiscount: product?.discountConfig?.cartMaxDiscount,
    };
    this.trackUnified('product_updated_in_cart', {
      ...(flow ? { flow, outcome: 'success' } : {}),
      discountConfig,
      cartDetails,
    });
  }

  trackResponseError(error: any, context?: any): void {
    const responseErrorDetails: any = this.convertKeysToSnakeCase(
      error?.responseErrorDetails || {}
    );
    this.trackUnified('response_error', {
      api_name: error?.apiName,
      error_message: error?.message,
      error_stack: error?.stack,
      response_details: responseErrorDetails,
      status_code: error?.statusCode,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }

  // Dismiss-detection heuristic mirrors PaymentService.looksLikeDismiss.
  // Inlined here to avoid pulling PaymentService into the analytics dependency
  // graph (would create a circular import).
  private looksLikeDismissError(error: any): boolean {
    if (!error) return false;
    const msg = String(error?.message ?? error ?? '').toLowerCase();
    const code = String(error?.code ?? '').toLowerCase();
    const name = String(error?.name ?? '').toLowerCase();
    const haystack = `${msg} ${code} ${name}`;
    return (
      haystack.includes('cancel') ||
      haystack.includes('dismiss') ||
      haystack.includes('closed') ||
      haystack.includes('aborted') ||
      haystack.includes('user_action') ||
      haystack.includes('not_presented')
    );
  }

  trackPaymentError(error: any, context?: any): void {
    // User closed the payment sheet without paying = cancellation, not error.
    // Route to payment_cancelled so it doesn't pollute payment_error analytics.
    const flow = context?.flow;
    if (this.looksLikeDismissError(error)) {
      this.trackUnified('payment_cancelled', {
        ...(flow ? { flow } : {}),
        outcome: 'abandon',
        legacy_event_name: 'Payment Cancelled (Caught Exception)',
        reason: error?.message || 'payment_dismissed',
        booking_id: context?.bookingId,
      });
      return;
    }
    this.trackUnified('payment_error', {
      ...(flow ? { flow } : {}),
      error_message: error?.message ?? String(error),
      error_code: error?.code,
      customer: context?.customer,
      payment_intent: context?.paymentIntent,
      booking_id: context?.bookingId,
    });
  }

  /**
   * Build a PII-safe summary of flight passengers + trip context for funnel
   * events. Replaces the previous practice of spreading the entire passenger
   * array (which included names, DOBs, passport numbers, IdDetails — all PII
   * and noise for analytics).
   *
   * Pass the raw passenger list plus the flight context. Returns:
   *   { adults, children, infants, total, trip_type, is_international,
   *     add_ons_selected: ['seats', 'meals', 'baggage'] }
   */
  buildFlightPassengerSummary(opts: {
    passengers?: any[];
    flightData?: any;
    origin?: string;
    destination?: string;
    addOnsPrices?: { Seats?: number; Meal?: number; Baggage?: number };
  }): {
    adults: number;
    children: number;
    infants: number;
    total: number;
    trip_type: string;
    is_international: boolean | null;
    add_ons_selected: string[];
  } {
    const passengers = Array.isArray(opts?.passengers) ? opts.passengers : [];
    let adults = 0;
    let children = 0;
    let infants = 0;
    for (const p of passengers) {
      const type = String(p?.type || p?.Type || '').toLowerCase();
      if (type === 'adult') adults++;
      else if (type === 'child') children++;
      else if (type === 'infant') infants++;
    }

    // Derive trip type. JourneyType: 1=oneway, 2=return, 3=multi-city in our APIs.
    const jt = opts?.flightData?.JourneyType;
    const tripType =
      jt === 2 || jt === '2'
        ? 'return'
        : jt === 3 || jt === '3'
        ? 'multi_city'
        : 'oneway';

    // Heuristic: international if origin and destination country differ.
    // Falls back to null if we can't determine — never assert false.
    let isInternational: boolean | null = null;
    const origin = String(
      opts?.origin || opts?.flightData?.Origin || ''
    ).toUpperCase();
    const destination = String(
      opts?.destination || opts?.flightData?.Destination || ''
    ).toUpperCase();
    const originCountry = opts?.flightData?.OriginCountry || null;
    const destCountry = opts?.flightData?.DestinationCountry || null;
    if (originCountry && destCountry) {
      isInternational = originCountry !== destCountry;
    } else if (origin && destination && origin.length >= 3 && destination.length >= 3) {
      // No country info — leave null. Airport-code prefix comparison is
      // unreliable (e.g. JFK vs LHR are both 3-letter codes).
      isInternational = null;
    }

    const addOnsSelected: string[] = [];
    const prices = opts?.addOnsPrices || {};
    if ((prices.Seats || 0) > 0) addOnsSelected.push('seats');
    if ((prices.Meal || 0) > 0) addOnsSelected.push('meals');
    if ((prices.Baggage || 0) > 0) addOnsSelected.push('baggage');

    return {
      adults,
      children,
      infants,
      total: adults + children + infants,
      trip_type: tripType,
      is_international: isInternational,
      add_ons_selected: addOnsSelected,
    };
  }

  private getFlightFunnelContext(
    step: string,
    stepIndex: number,
    outcome = 'na',
    flowType?: string
  ) {
    return {
      flow: this.resolveFlightFlow(flowType),
      step,
      step_index: stepIndex,
      outcome,
    };
  }

  private resolveFlightFlow(flowType?: string): string {
    const normalized = toSnakeCase(flowType || '');
    if (!normalized) return 'flight';
    if (KNOWN_FLIGHT_FLOWS.has(normalized)) return normalized;
    // Accept anything ending in _flight as a recognized variant
    // (e.g. future "premium_flight"), otherwise fall back to the base flow.
    if (normalized.endsWith('_flight') || normalized === 'flight') {
      return normalized;
    }
    return 'flight';
  }

  // Flights: Single consolidated search event via trackEvent (Mixpanel + GA)
  trackFlightSearchEvent(params: {
    tripType: 'one-way' | 'return' | 'one_way' | 'return';
    departureCity?: string;
    departureCode?: string;
    arrivalCity?: string;
    arrivalCode?: string;
    departureDate: string | Date;
    returnDate?: string | Date | null;
    adults: number;
    children: number;
    infants: number;
    cabinClass: string;
    airlinePreference: any;
    queryUrl: string;
    flowType?: string;
  }): void {
    const toYmd = (d: string | Date | null | undefined) => {
      if (!d) return null;
      const date = typeof d === 'string' ? new Date(d) : d;
      return Number.isNaN(date.getTime())
        ? null
        : date.toISOString().substring(0, 10);
    };
    const mapTrip = (t: string) => (t === 'return' ? 'return' : 'one-way');
    const mapCabin = (c: string) => {
      const v = (c || '').toLowerCase();
      if (v.includes('business')) return 'business';
      if (v.includes('first')) return 'first';
      return 'economy';
    };

    this.trackUnified('search_started', {
      ...this.getFlightFunnelContext('search', 1, 'success', params?.flowType),
      legacy_event_name: `${params?.flowType} Search Button Clicked`,
      trip_type: mapTrip(params?.tripType as string),
      flow_type: params?.flowType || 'Flight',
      departure_city: params?.departureCity,
      departure_airport_code: params?.departureCode,
      arrival_city: params?.arrivalCity,
      arrival_airport_code: params?.arrivalCode,
      departure_date: toYmd(params?.departureDate),
      return_date: toYmd(params?.returnDate) || null,
      travellers: {
        adults: Number(params?.adults) || 0,
        children: Number(params?.children) || 0,
        infants: Number(params?.infants) || 0,
      },
      cabin_class: mapCabin(params?.cabinClass),
      airlinePreference: params?.airlinePreference,
      queryUrl: params?.queryUrl,
    });
  }

  trackAirportEvent(EventTitle, type, airportCode): void {
    this.trackUnified(EventTitle, {
      type: type,
      airport_code: airportCode,
    });
  }

  trackFlightSearchResultsFilterEvent(
    selectedFilterArray,
    flowType?: string
  ): void {
    console.log(selectedFilterArray);
    this.trackUnified('results_filter_changed', {
      ...this.getFlightFunnelContext('results', 2, 'na', flowType),
      legacy_event_name: `${flowType} Search Results Filter Changed`,
      selected_filter: selectedFilterArray,
      currency: 'USD',
    });
  }

  trackFlightSearchResultsSortEvent(selectedSort, flowType?: string): void {
    this.trackUnified('results_sort_changed', {
      ...this.getFlightFunnelContext('results', 2, 'na', flowType),
      legacy_event_name: `${flowType} Search Results Sort Changed`,
      selected_sort: selectedSort,
      currency: 'USD',
    });
  }

  trackFlightSearchResultsCardSelectEvent(
    EventTitle,
    flightData,
    segmentIndex,
    segmentId
  ): void {
    const segs = flightData?.segments;
    const travelDateSrc =
      flightData?.TravelDate || segs?.[0]?.departure?.time;
    let travelDateIso: string | null = null;
    if (travelDateSrc) {
      const d = new Date(travelDateSrc);
      if (!Number.isNaN(d.getTime())) {
        travelDateIso = d.toISOString();
      }
    }

    this.trackUnified('item_selected', {
      ...this.getFlightFunnelContext('details', 3, 'success'),
      legacy_event_name: EventTitle,
      segment_index: segmentIndex,
      segment_id: segmentId,
      origin: flightData?.Origin || segs?.[0]?.departure?.airportCode,
      destination:
        flightData?.Destination || segs?.[segs.length - 1]?.arrival?.airportCode,
      fare_types: flightData?.FareTypes,
      is_lcc: flightData?.IsLcc,
      travel_date: travelDateIso,
      journey_type: flightData?.JourneyType,
      fare: {
        final_price: flightData?.Fare?.priceDetail?.finalPrice,
        currency:
          flightData?.Fare?.priceDetail?.currency ??
          flightData?.displayPricing?.pricing?.currency,
        tax: flightData?.Fare?.priceDetail?.tax,
        total_price:
          flightData?.Fare?.priceDetail?.totalPrice ??
          flightData?.displayPricing?.pricing?.totalAmount,
        total_discount: flightData?.Fare?.priceDetail?.totalDiscount,
      },
    });
  }

  /**
   * Fires when user clicks "Proceed to Review" on the SRP. This is intent —
   * canonical `checkout_started` fires on the review-page mount (see
   * trackFlightCheckoutStarted) so deeplink/back-nav users get counted too.
   */
  trackProceedToReviewEvent(
    eventTitle: string,
    flightData: any,
    route: string
  ): void {
    const segs = flightData?.segments;
    const travelDateSrc =
      flightData?.TravelDate || segs?.[0]?.departure?.time;
    let travelDateIso: string | null = null;
    if (travelDateSrc) {
      const d = new Date(travelDateSrc);
      if (!Number.isNaN(d.getTime())) {
        travelDateIso = d.toISOString();
      }
    }

    this.trackUnified('checkout_continue_clicked', {
      flow: 'flight',
      outcome: 'success',
      legacy_event_name: eventTitle,
      review_id: flightData?.ResultId || flightData?.searchId,
      origin: flightData?.Origin || segs?.[0]?.departure?.airportCode,
      destination:
        flightData?.Destination || segs?.[segs.length - 1]?.arrival?.airportCode,
      fare_types: flightData?.FareTypes,
      is_lcc: flightData?.IsLcc,
      travel_date: travelDateIso,
      journey_type: flightData?.JourneyType,
      total_fare: {
        final_price: flightData?.TotalFare?.priceDetail
          ? flightData?.TotalFare?.priceDetail?.finalPrice
          : flightData?.Fare?.priceDetail?.finalPrice,
        currency: flightData?.TotalFare?.priceDetail
          ? flightData?.TotalFare?.priceDetail?.currency
          : flightData?.Fare?.priceDetail?.currency ??
            flightData?.displayPricing?.pricing?.currency,
        tax: flightData?.TotalFare?.priceDetail
          ? flightData?.TotalFare?.priceDetail?.tax
          : flightData?.Fare?.priceDetail?.tax,
        total_price: flightData?.TotalFare?.priceDetail
          ? flightData?.TotalFare?.priceDetail?.totalPrice
          : flightData?.Fare?.priceDetail?.totalPrice ??
            flightData?.displayPricing?.pricing?.totalAmount,
        total_discount: flightData?.TotalFare?.priceDetail
          ? flightData?.TotalFare?.priceDetail?.totalDiscount
          : flightData?.Fare?.priceDetail?.totalDiscount,
      },
      route: route,
    });
  }

  /**
   * Canonical step-4 emit for flight v2. Call from booking-review.page.ts
   * ionViewDidEnter (or the equivalent modal mount).
   */
  trackFlightCheckoutStarted(opts: {
    flow?: string;
    reviewId?: string;
    flightData?: any;
    isFlexi?: boolean;
  }): void {
    const flow = opts?.flow || (opts?.isFlexi ? 'flexicancel' : 'flight');
    const fd = opts?.flightData;
    this.trackUnified('checkout_started', {
      flow,
      step: 'review',
      step_index: 4,
      outcome: 'success',
      legacy_event_name: 'Flight Booking Review Loaded',
      review_id: opts?.reviewId || fd?.ResultId,
      origin: fd?.Origin,
      destination: fd?.Destination,
      is_lcc: fd?.IsLcc,
      journey_type: fd?.JourneyType,
    });
  }

  trackReviewContinueEvent(EventTitle, message, reviewId): void {
    this.trackUnified('checkout_continue_clicked', {
      ...this.getFlightFunnelContext('review', 4, 'na'),
      legacy_event_name: EventTitle,
      review_id: reviewId,
    });
  }

  trackPassengerEvent(
    EventTitle,
    reviewId,
    passengerData,
    passengerIndex
  ): void {
    // No longer spread the full passenger record into the event (was PII +
    // payload bloat). Carry: action (added vs updated), passenger_type, index,
    // and the slim getAnalyticsPassengerObject fields (gender/age/isLeadPax)
    // when present.
    const title = String(EventTitle || '');
    const action = /\bAdded\b/i.test(title)
      ? 'added'
      : /\bUpdated\b/i.test(title)
      ? 'updated'
      : 'updated';
    const pType = String(
      passengerData?.type || passengerData?.Type || ''
    ).toLowerCase();
    this.trackUnified('passenger_updated', {
      flow: 'flight',
      outcome: 'success',
      legacy_event_name: EventTitle,
      review_id: reviewId,
      action,
      passenger_index: passengerIndex,
      passenger_type: pType || undefined,
      // Slim subset of passenger details (no names/DOB/passport).
      gender: passengerData?.gender,
      age: passengerData?.age,
      is_lead_pax: passengerData?.isLeadPax,
    });
  }

  trackAddOnsEvent(EventTitle, reviewId, addOnsData): void {
    // Reduced payload: top-level totals only. Per-seat / per-meal selections
    // belong on their own `addon_*_selected` events fired from the addons
    // modal — they're not part of the passenger-update event.
    this.trackUnified('addons_updated', {
      flow: 'flight',
      outcome: 'na',
      legacy_event_name: EventTitle,
      review_id: reviewId,
      seats_total: addOnsData?.Seats || addOnsData?.seats_total || 0,
      meals_total: addOnsData?.Meal || addOnsData?.meals_total || 0,
      baggage_total: addOnsData?.Baggage || addOnsData?.baggage_total || 0,
    });
  }

  /**
   * @deprecated Use the canonical prebook_initiated / prebook_validated /
   * prebook_failed events instead — they carry funnel context and respond
   * to the create_booking outcome. This helper used to mislabel itself as
   * `payment` step 5 and spread the entire booking payload. Kept temporarily
   * as a no-op to avoid breaking callsites during migration.
   */
  trackCreateFlightBookingEvent(_data: any): void {
    // intentionally a no-op — see deprecation notice above.
  }

  /**
   * @deprecated Use the canonical `booking_initiated` event instead. This
   * helper used to fire `payment_attempted` from inside handleBooking() —
   * which executes AFTER payment_success, producing wrong-order events
   * (payment_attempted after payment_success). Kept temporarily as a no-op.
   */
  trackHandleFlightBook(_data: any, _flowType?: string): void {
    // intentionally a no-op — see deprecation notice above.
  }

  trackFlightSendEmailEvent(bookingId, flowType?: string): void {
    this.trackUnified('booking_email_clicked', {
      ...this.getFlightFunnelContext('confirmation', 6, 'na', flowType),
      legacy_event_name: `${flowType ?? 'Flight'} Send Email`,
      booking_id: bookingId,
    });
  }

  trackCancelFlightBookingEvent(bookingId, flowType?: string): void {
    this.trackUnified('booking_cancel_clicked', {
      ...this.getFlightFunnelContext('confirmation', 6, 'abandon', flowType),
      legacy_event_name: `${flowType ?? 'Flight'} Cancel Booking`,
      booking_id: bookingId,
    });
  }

  trackFlightReleaseBookingEvent(bookingId, flowType?: string): void {
    this.trackUnified('booking_release_clicked', {
      ...this.getFlightFunnelContext('confirmation', 6, 'na', flowType),
      legacy_event_name: `${flowType ?? 'Flight'} Release Booking`,
      booking_id: bookingId,
    });
  }

  trackFlightViewDetailsEvent(bookingId, flowType?: string): void {
    this.trackUnified('booking_view_details_clicked', {
      ...this.getFlightFunnelContext('confirmation', 6, 'na', flowType),
      legacy_event_name: `${flowType ?? 'Flight'} View Details`,
      booking_id: bookingId,
    });
  }

  // Hotels: Track when a user selects a hotel

  private getHotelFunnelContext(
    step: string,
    stepIndex: number,
    outcome = 'na',
    flow: string = 'hotel'
  ) {
    return {
      flow,
      step,
      step_index: stepIndex,
      outcome,
    };
  }

  trackHotelCitySearch(searchData: any): void {
    // Picking a city in the autocomplete is an input interaction, not a search
    // commitment. The canonical `search_started` fires on submitSearchQuery
    // (Search button click).
    this.trackUnified('search_input_selected', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'Selected Hotel/City',
      address: searchData?.address,
      aiportCodeCity: searchData?.aiportCodeCity,
      airportCode: searchData?.airportCode,
      city: searchData?.city,
      cityCountry: searchData?.cityCountry,
      country: searchData?.country,
      countryCode: searchData?.countryCode,
      entityId: searchData?.entityId,
      hotelCodes: searchData?.hotelCodes,
      id: searchData?.id,
      title: searchData?.title,
      type: searchData?.type,
      token: searchData?.token,
    });
  }

  // Hotel Search Results: Track page load
  trackHotelSearchResultsPageLoad(
    searchQuery: any,
    numberOfNights: number,
    flow: string = 'hotel'
  ): void {
    // SRP-load is not a search commitment — search_started already fired on
    // the search button click. This is a diagnostic non-funnel event that
    // captures the search context the user landed with.
    this.trackUnified('search_results_page_loaded', {
      flow,
      outcome: 'success',
      legacy_event_name: 'Hotel Search Results Loaded',
      search_parameters: {
        destination: searchQuery?.entityLabel || '',
        destination_type: searchQuery?.type || '',
        check_in_date: searchQuery?.checkin || '',
        check_out_date: searchQuery?.checkout || '',
        number_of_nights: numberOfNights,
        total_rooms: searchQuery?.rooms?.length || 0,
        total_guests:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.adults || 0) + (room.children || 0),
            0
          ) || 0,
        adults:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.adults || 0),
            0
          ) || 0,
        children:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.children || 0),
            0
          ) || 0,
        room_details: searchQuery?.rooms || [],
        currency: 'USD',
        active_filters: Object.keys(searchQuery?.fq || {}),
        request_id: searchQuery?.requestId,
      },
      page_context: {
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        page_url: window.location.href,
      },
    });
  }

  // Hotel Search Results: Track navigation events
  trackHotelSearchResultsNavigation(
    eventType: 'back' | 'edit',
    searchQuery: any,
    hotelList: any[],
    selectedSort: string
  ): void {
    const legacyEventName =
      eventType === 'back'
        ? 'Hotel Search Results Back Clicked'
        : 'Hotel Search Results Edit Clicked';

    this.trackUnified('search_navigation_clicked', {
      ...this.getHotelFunnelContext('results', 2, 'na'),
      legacy_event_name: legacyEventName,
      action_type: eventType,
      destination_page: '/hotel/search',
      current_search: {
        destination: searchQuery?.entityLabel || '',
        check_in: searchQuery?.checkin || '',
        check_out: searchQuery?.checkout || '',
        guests:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.adults || 0) + (room.children || 0),
            0
          ) || 0,
        rooms: searchQuery?.rooms?.length || 0,
        total_results: hotelList?.length || 0,
        active_filters: Object.keys(searchQuery?.fq || {}).length,
        current_sort: selectedSort || 'relevance',
      },
    });
  }

  // Hotel Search Results: Track filter modal events
  trackHotelSearchResultsFilterOpened(
    searchQuery: any,
    filters: any[],
    hotelList: any[]
  ): void {
    this.trackUnified('results_filter_opened', {
      ...this.getHotelFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Hotel Search Results Filter Opened',
      button: 'filter-button',
      current_filters: searchQuery.fq || {},
      available_filters: filters?.map((f) => f.name) || [],
      total_hotels: hotelList?.length || 0,
      search_context: {
        destination: searchQuery?.entityLabel || '',
        check_in: searchQuery?.checkin || '',
        check_out: searchQuery?.checkout || '',
        guests:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.adults || 0) + (room.children || 0),
            0
          ) || 0,
        rooms: searchQuery?.rooms?.length || 0,
      },
    });
  }

  trackHotelSearchResultsFilterChanged(
    previousFilters: any,
    selectedFilters: any,
    searchQuery: any,
    hotelList: any[]
  ): void {
    const getFilterDifferences = (oldFilters: any, newFilters: any): any => {
      const differences = {};
      Object.keys(newFilters).forEach((key) => {
        if (
          !oldFilters[key] ||
          JSON.stringify(oldFilters[key]) !== JSON.stringify(newFilters[key])
        ) {
          differences[key] = newFilters[key];
        }
      });
      return differences;
    };

    this.trackUnified('results_filter_changed', {
      ...this.getHotelFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Hotel Search Results Filter Changed',
      previous_filters: previousFilters,
      selected_filters: selectedFilters,
      filters_added: getFilterDifferences(previousFilters, selectedFilters),
      filters_removed: getFilterDifferences(selectedFilters, previousFilters),
      total_active_filters: Object.keys(selectedFilters).length,
      search_context: {
        destination: searchQuery?.entityLabel || '',
        check_in: searchQuery?.checkin || '',
        check_out: searchQuery?.checkout || '',
        total_results: hotelList?.length || 0,
      },
    });
  }

  trackHotelSearchResultsFilterDismissed(currentFilters: any): void {
    this.trackUnified('results_filter_dismissed', {
      ...this.getHotelFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Hotel Search Results Filter Dismissed',
      action: 'dismissed_without_changes',
      current_filters: currentFilters,
    });
  }

  // Hotel Search Results: Track sort modal events
  trackHotelSearchResultsSortOpened(
    selectedSort: string,
    sortList: any[],
    searchQuery: any,
    hotelList: any[]
  ): void {
    this.trackUnified('results_sort_opened', {
      ...this.getHotelFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Hotel Search Results Sort Opened',
      button: 'sort-button',
      current_sort: selectedSort || 'relevance',
      available_sorts: sortList?.map((s) => s.name) || [],
      total_hotels: hotelList?.length || 0,
      search_context: {
        destination: searchQuery?.entityLabel || '',
        check_in: searchQuery?.checkin || '',
        check_out: searchQuery?.checkout || '',
        guests:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.adults || 0) + (room.children || 0),
            0
          ) || 0,
        current_filters: Object.keys(searchQuery.fq || {}),
      },
    });
  }

  trackHotelSearchResultsSortChanged(
    previousSort: string,
    selectedSort: string,
    sortKey: string,
    searchQuery: any,
    hotelList: any[]
  ): void {
    this.trackUnified('results_sort_changed', {
      ...this.getHotelFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Hotel Search Results Sort Changed',
      previous_sort: previousSort,
      selected_sort: selectedSort,
      sort_key: sortKey,
      total_hotels: hotelList?.length || 0,
      search_context: {
        destination: searchQuery?.entityLabel || '',
        active_filters: Object.keys(searchQuery.fq || {}).length,
      },
    });
  }

  trackHotelSearchResultsSortDismissed(currentSort: string): void {
    this.trackUnified('results_sort_dismissed', {
      ...this.getHotelFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Hotel Search Results Sort Dismissed',
      action: 'dismissed_without_changes',
      current_sort: currentSort || 'relevance',
    });
  }

  // Hotel Search Results: Track search results events
  trackHotelSearchResultsSuccess(
    response: any,
    searchQuery: any,
    loadTime: number,
    selectedSort: string,
    flow: string = 'hotel'
  ): void {
    this.trackUnified('results_loaded', {
      ...this.getHotelFunnelContext('results', 2, 'success', flow),
      legacy_event_name: 'Hotel Search Results Success',
      results_count: response?.content?.length || 0,
      total_available: response?.totalElements || 0,
      load_time_ms: loadTime,
      has_filters: response?.filters?.length > 0,
      has_sorts: response?.sorts?.length > 0,
      is_last_page: response?.last || false,
      search_context: {
        destination: searchQuery?.entityLabel || '',
        request_id: searchQuery?.requestId,
        offset: searchQuery?.offset || 0,
        sort_option: selectedSort || 'relevance',
        active_filters: Object.keys(searchQuery?.fq || {}).length,
      },
    });
  }

  trackHotelSearchResultsEmpty(
    searchQuery: any,
    flow: string = 'hotel'
  ): void {
    this.trackUnified('results_loaded', {
      ...this.getHotelFunnelContext('results', 2, 'error', flow),
      legacy_event_name: 'Hotel Search Results Empty',
      error_code: 'no_results',
      error_message: 'No hotels found for the selected search parameters',
      search_parameters: {
        destination: searchQuery?.entityLabel || '',
        check_in: searchQuery?.checkin || '',
        check_out: searchQuery?.checkout || '',
        guests:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.adults || 0) + (room.children || 0),
            0
          ) || 0,
        active_filters: Object.keys(searchQuery?.fq || {}),
      },
      suggestions: {
        try_different_dates: true,
        remove_filters: Object.keys(searchQuery?.fq || {}).length > 0,
        expand_search_area: true,
      },
    });
  }

  trackHotelSearchResultsError(
    error: any,
    searchQuery: any,
    loadTime: number,
    selectedSort: string,
    flow: string = 'hotel'
  ): void {
    this.trackUnified('results_loaded', {
      ...this.getHotelFunnelContext('results', 2, 'error', flow),
      legacy_event_name: 'Hotel Search Results Error',
      error_message: error?.message || 'Unknown error',
      load_time_ms: loadTime,
      search_context: {
        destination: searchQuery?.entityLabel || '',
        request_id: searchQuery?.requestId,
        offset: searchQuery?.offset || 0,
        sort_option: selectedSort || 'relevance',
      },
    });
  }

  // Hotel Search Results: Track pagination events
  trackHotelSearchResultsPagination(
    action: string,
    currentCount: number,
    offset: number,
    searchQuery: any,
    selectedSort: string,
    flow: string = 'hotel'
  ): void {
    this.trackUnified('results_pagination_triggered', {
      ...this.getHotelFunnelContext('results', 2, 'na', flow),
      legacy_event_name: 'Hotel Search Results Pagination',
      action: action,
      current_page_size: currentCount,
      offset: offset,
      search_context: {
        destination: searchQuery?.entityLabel || '',
        active_filters: Object.keys(searchQuery?.fq || {}).length,
        sort_option: selectedSort || 'relevance',
      },
    });
  }

  trackHotelSearchResultsPaginationSuccess(
    newResultsCount: number,
    totalResults: number,
    isLastPage: boolean,
    flow: string = 'hotel'
  ): void {
    this.trackUnified('results_pagination_loaded', {
      ...this.getHotelFunnelContext('results', 2, 'success', flow),
      legacy_event_name: 'Hotel Search Results Pagination Success',
      action: 'loaded_more_results',
      new_results_count: newResultsCount,
      total_results: totalResults,
      is_last_page: isLastPage,
    });
  }

  trackHotelSearchResultsPaginationEmpty(
    totalResults: number,
    flow: string = 'hotel'
  ): void {
    this.trackUnified('results_pagination_loaded', {
      ...this.getHotelFunnelContext('results', 2, 'error', flow),
      legacy_event_name: 'Hotel Search Results Pagination Empty',
      error_code: 'no_more_results',
      action: 'no_more_results',
      total_results: totalResults,
    });
  }

  // Hotel Search Results: Track hotel selection
  trackHotelSearchResultsCardSelectEvent(
    hotelDetail: any,
    roomDetail: any,
    hotelPricingInfo: any,
    numberOfNights: number,
    hotelSearchQuery: any
  ): void {
    this.trackUnified('item_selected', {
      ...this.getHotelFunnelContext('details', 3, 'success'),
      legacy_event_name: 'Hotel Selected',
      component: 'hotel-card-component',
      button: 'book-now-button',
      hotel_details: {
        hotel_code: hotelDetail?.hotelCode,
        hotel_name: hotelDetail?.hotelName,
        star_rating: hotelDetail?.starRating,
        price:
          roomDetail?.priceDetail?.totalPrice ||
          hotelPricingInfo?.priceDetail?.totalPrice,
        currency:
          roomDetail?.priceDetail?.currency ||
          hotelPricingInfo?.priceDetail?.currency,
        final_price:
          roomDetail?.priceDetail?.finalPrice ||
          hotelPricingInfo?.priceDetail?.finalPrice,
        original_price:
          roomDetail?.priceDetail?.originalPrice ||
          hotelPricingInfo?.priceDetail?.originalPrice,
        discount:
          roomDetail?.priceDetail?.discount ||
          hotelPricingInfo?.priceDetail?.discount,
        location: hotelDetail?.location,
        city: hotelDetail?.city,
        country: hotelDetail?.country,
        facilities: hotelDetail?.facilitiesIcon?.slice(0, 5),
        tags: hotelDetail?.tags
          ? Object.values(hotelDetail?.tags)?.slice(0, 3)
          : [],
        image_available: !!hotelDetail?.images?.[0],
        number_of_nights: numberOfNights,
      },
      search_context: {
        search_query: hotelSearchQuery,
        user_session_timestamp: new Date().toISOString(),
      },
    });
    // Removed `details_view_started` here — it duplicated `item_selected`
    // above at the same moment + same step. `details_view_loaded` still fires
    // when the details page finishes loading (carries data-load timing).
  }

  // Hotel Details: Track page load and user interactions
  trackHotelDetailsPageLoad(
    hotelDetail: any,
    priceDetails: any,
    numberOfNights: number,
    hotelSearchQuery: any
  ): void {
    this.trackUnified('details_view_loaded', {
      ...this.getHotelFunnelContext('details', 3, 'success'),
      legacy_event_name: 'Hotel Details Page Loaded',
      hotel_details: {
        hotel_code: hotelDetail?.hotelCode,
        hotel_name: hotelDetail?.hotelName,
        star_rating: hotelDetail?.starRating || hotelDetail?.hotelRating,
        location: hotelDetail?.location || hotelDetail?.address,
        city: hotelDetail?.city,
        country: hotelDetail?.country,
        total_images: hotelDetail?.images?.length || 0,
        amenities_count: hotelDetail?.amenities?.length || 0,
        facilities_count: hotelDetail?.facilitiesIcon?.length || 0,
      },
      pricing_details: {
        total_price: priceDetails?.totalPrice,
        currency: priceDetails?.currency,
        discount_available: !!priceDetails?.discount?.title,
        discount_title: priceDetails?.discount?.title,
        special_discount: priceDetails?.specialDiscount?.description,
        campaigns:
          priceDetails?.campaigns?.map((c) => ({
            campaign_slug: c?.campaignSlug,
            promo_text: c?.promoText,
          })) || [],
      },
      search_context: {
        number_of_nights: numberOfNights,
        search_query: hotelSearchQuery,
        source_page: 'search_results',
      },
      page_context: {
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        page_url: window.location.href,
      },
    });
  }

  trackHotelDetailsImageInteraction(
    imageIndex: number,
    totalImages: number,
    hotelCode: string
  ): void {
    this.trackUnified('details_image_clicked', {
      ...this.getHotelFunnelContext('details', 3, 'na'),
      legacy_event_name: 'Hotel Details Image Clicked',
      image_index: imageIndex,
      total_images: totalImages,
      hotel_code: hotelCode,
      interaction_type: 'image_gallery',
    });
  }

  trackHotelDetailsAmenitiesToggle(
    isVisible: boolean,
    hotelCode: string
  ): void {
    this.trackUnified('details_amenities_toggled', {
      ...this.getHotelFunnelContext('details', 3, 'na'),
      legacy_event_name: 'Hotel Details Amenities Toggled',
      action: isVisible ? 'show_amenities' : 'hide_amenities',
      hotel_code: hotelCode,
    });
  }

  trackHotelDetailsOverviewToggle(isVisible: boolean, hotelCode: string): void {
    this.trackUnified('details_overview_toggled', {
      ...this.getHotelFunnelContext('details', 3, 'na'),
      legacy_event_name: 'Hotel Details Overview Toggled',
      action: isVisible ? 'show_overview' : 'hide_overview',
      hotel_code: hotelCode,
    });
  }

  trackHotelDetailsBackNavigation(hotelCode: string, timeSpent: number): void {
    this.trackUnified('details_back_clicked', {
      ...this.getHotelFunnelContext('details', 3, 'na'),
      legacy_event_name: 'Hotel Details Back Clicked',
      hotel_code: hotelCode,
      time_spent_seconds: timeSpent,
      destination_page: 'search_results',
    });
  }

  trackHotelDetailsSelectRoom(
    hotelDetail: any,
    priceDetails: any,
    numberOfNights: number
  ): void {
    // User tapped the "Select Room" button on the hotel details page, which
    // opens the rooms modal. This is NOT checkout_continue_clicked — they
    // haven't picked a room yet, and the rooms modal isn't checkout. The
    // canonical continue-to-checkout signal fires when they pick a specific
    // room and proceed to the review page (trackRoomSelected → step 4).
    this.trackUnified('rooms_view_opened', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'Hotel Details Select Room Clicked',
      hotel_details: {
        hotel_code: hotelDetail?.hotelCode,
        hotel_name: hotelDetail?.hotelName,
        star_rating: hotelDetail?.starRating || hotelDetail?.hotelRating,
      },
      pricing_details: {
        total_price: priceDetails?.totalPrice,
        currency: priceDetails?.currency,
        number_of_nights: numberOfNights,
      },
      action: 'open_room_selection',
    });
  }

  // Room Selection Modal: Track user interactions
  trackRoomSelectionModalOpened(
    roomsCount: number,
    hotelCode: string,
    searchQuery: any
  ): void {
    this.trackUnified('room_selection_opened', {
      ...this.getHotelFunnelContext('details', 3, 'na'),
      legacy_event_name: 'Room Selection Modal Opened',
      available_rooms: roomsCount,
      hotel_code: hotelCode,
      search_context: {
        room_count: searchQuery?.rooms?.length || 0,
        guest_count:
          searchQuery?.rooms?.reduce(
            (total, room) => total + (room.adults || 0) + (room.children || 0),
            0
          ) || 0,
        check_in: searchQuery?.checkin,
        check_out: searchQuery?.checkout,
      },
    });
  }

  trackRoomSelectionModalClosed(
    hotelCode: string,
    wasRoomSelected: boolean,
    timeSpent?: number
  ): void {
    this.trackUnified('room_selection_closed', {
      ...this.getHotelFunnelContext(
        'details',
        3,
        wasRoomSelected ? 'success' : 'abandon'
      ),
      legacy_event_name: 'Room Selection Modal Closed',
      hotel_code: hotelCode,
      room_selected: wasRoomSelected,
      time_spent_seconds: timeSpent || 0,
      action: wasRoomSelected ? 'room_selected' : 'modal_dismissed',
    });
  }

  trackRoomSelected(
    roomDetail: any,
    hotelCode: string,
    roomIndex?: number
  ): void {
    // `room_selected` is a hotel-specific enrichment event that fires within
    // step 3 (details). It is the success outcome of the details step that
    // takes the user toward step 4 (review) — but it is NOT itself a canonical
    // funnel event. current_funnel_step / current_funnel_step_index are
    // auto-attached by trackUnified from the last canonical funnel event.
    this.trackUnified('room_selected', {
      flow: 'hotel',
      outcome: 'success',
      legacy_event_name: 'Room Selected',
      hotel_code: hotelCode,
      room_index: roomIndex,
      room_details: {
        room_name: roomDetail?.Name?.[0] || roomDetail?.name,
        booking_code: roomDetail?.BookingCode,
        price: roomDetail?.Price?.amount || roomDetail?.price,
        currency: roomDetail?.Price?.currency || roomDetail?.currency,
        bed_type: roomDetail?.BedType || roomDetail?.bedType,
        occupancy: roomDetail?.Occupancy || roomDetail?.occupancy,
        room_size: roomDetail?.RoomSize || roomDetail?.roomSize,
        amenities: roomDetail?.amenities?.slice(0, 5) || [],
        cancellation_policy: !!roomDetail?.CancelPolicies,
      },
      action: 'room_confirmed',
    });
  }

  // Hotel Booking Review: Track page and form interactions
  trackHotelBookingReviewPageLoad(
    bookingId: string,
    hotelDetails: any,
    roomsInfo: any,
    salesSummary: any
  ): void {
    this.trackUnified('checkout_started', {
      ...this.getHotelFunnelContext('review', 4, 'success'),
      legacy_event_name: 'Hotel Booking Review Page Loaded',
      booking_id: bookingId,
      hotel_details: {
        hotel_code: hotelDetails?.hotelCode,
        hotel_name: hotelDetails?.hotelName,
        star_rating: hotelDetails?.starRating || hotelDetails?.hotelRating,
        location: hotelDetails?.location || hotelDetails?.address,
      },
      room_details: {
        room_name: roomsInfo?.roomName || roomsInfo?.name,
        price: roomsInfo?.priceDetail?.totalPrice,
        currency: roomsInfo?.priceDetail?.currency,
        discount: roomsInfo?.priceDetail?.discount,
        original_price: roomsInfo?.priceDetail?.originalPrice,
      },
      sales_summary: {
        total_amount: salesSummary?.totalAmount,
        tax_amount: salesSummary?.taxAmount,
        base_amount: salesSummary?.baseAmount,
        currency: salesSummary?.currency,
      },
      page_context: {
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        source_page: 'room_selection',
      },
    });
  }

  trackHotelBookingReviewFormValidation(
    isValid: boolean,
    errorFields: string[],
    bookingId: string
  ): void {
    this.trackUnified('checkout_form_validated', {
      ...this.getHotelFunnelContext('review', 4, isValid ? 'success' : 'error'),
      legacy_event_name: 'Hotel Booking Review Form Validation',
      booking_id: bookingId,
      form_valid: isValid,
      error_fields: errorFields,
      total_errors: errorFields.length,
      action: isValid ? 'form_valid' : 'validation_errors',
    });
  }

  trackHotelBookingReviewGuestFormInteraction(
    guestIndex: number,
    roomIndex: number,
    fieldName: string,
    bookingId: string
  ): void {
    this.trackUnified('checkout_guest_form_interaction', {
      ...this.getHotelFunnelContext('review', 4, 'na'),
      legacy_event_name: 'Hotel Booking Review Guest Form Interaction',
      booking_id: bookingId,
      guest_index: guestIndex,
      room_index: roomIndex,
      field_name: fieldName,
      action: 'field_interaction',
    });
  }

  trackHotelBookingReviewOfferSelection(
    offerId: number,
    offerTitle: string,
    bookingId: string,
    previousOfferId?: number,
    source: 'user' | 'auto' = 'user'
  ): void {
    const eventName =
      source === 'auto' ? 'checkout_offer_auto_applied' : 'checkout_offer_selected';
    this.trackUnified(eventName, {
      flow: 'hotel',
      outcome: source === 'auto' ? 'na' : 'success',
      legacy_event_name:
        source === 'auto'
          ? 'Hotel Booking Review Offer Auto Applied'
          : 'Hotel Booking Review Offer Selected',
      booking_id: bookingId,
      offer_id: offerId,
      offer_title: offerTitle,
      previous_offer_id: previousOfferId,
      action: source === 'auto' ? 'offer_auto_applied' : 'offer_changed',
      source,
    });
  }

  trackHotelBookingReviewCountryCodeChange(
    countryCode: string,
    bookingId: string
  ): void {
    this.trackUnified('checkout_country_code_changed', {
      ...this.getHotelFunnelContext('review', 4, 'na'),
      legacy_event_name: 'Hotel Booking Review Country Code Changed',
      booking_id: bookingId,
      country_code: countryCode,
      action: 'country_code_selected',
    });
  }

  trackHotelBookingReviewProceedToPayment(
    bookingId: string,
    totalAmount: number,
    currency: string,
    guestCount: number
  ): void {
    this.trackUnified('payment_attempted', {
      ...this.getHotelFunnelContext('payment', 5, 'success'),
      legacy_event_name: 'Hotel Booking Review Proceed To Payment',
      booking_id: bookingId,
      payment_details: {
        total_amount: totalAmount,
        currency: currency,
        guest_count: guestCount,
      },
      action: 'payment_initiated',
    });
  }

  trackHotelBookingReviewError(
    error: any,
    bookingId: string,
    context?: string
  ): void {
    this.trackUnified('checkout_error', {
      ...this.getHotelFunnelContext('review', 4, 'error'),
      legacy_event_name: 'Hotel Booking Review Error',
      booking_id: bookingId,
      error_message: error?.message || 'Unknown error',
      error_context: context,
      timestamp: new Date().toISOString(),
    });
  }

  trackHotelBookingReviewBackNavigation(
    bookingId: string,
    timeSpent: number
  ): void {
    this.trackUnified('checkout_back_clicked', {
      ...this.getHotelFunnelContext('review', 4, 'abandon'),
      legacy_event_name: 'Hotel Booking Review Back Clicked',
      booking_id: bookingId,
      time_spent_seconds: timeSpent,
      destination_page: 'hotel_search',
      action: 'navigation_back',
    });
  }

  //Bus
  private getBusFunnelContext(step: string, stepIndex: number, outcome = 'na') {
    return {
      flow: 'bus',
      step,
      step_index: stepIndex,
      outcome,
    };
  }

  trackBusSearchEvent(searchParams: any): void {
    this.trackUnified('search_started', {
      ...this.getBusFunnelContext('search', 1, 'success'),
      legacy_event_name: 'Bus Search Initiated',
      departure_city: searchParams?.departureCity,
      arrival_city: searchParams?.arrivalCity,
      departure_date: searchParams?.departureDate,
      return_date: searchParams?.returnDate || null,
      total_passengers: searchParams?.totalPassengers,
      adults: searchParams?.adults,
      children: searchParams?.children,
      currency: searchParams?.currency,
      query_url: searchParams?.queryUrl,
    });
  }

  trackBusSearchResultsSuccess(
    response: any,
    searchQuery: any,
    loadTime: number,
    selectedSort: string,
    page: number,
    pageSize: number
  ): void {
    this.trackUnified('results_loaded', {
      ...this.getBusFunnelContext('results', 2, 'success'),
      legacy_event_name: 'Bus Search Results Success',
      results_count: response?.content?.length || 0,
      total_available: response?.totalElements || 0,
      load_time_ms: loadTime,
      has_filters: response?.filters?.length > 0,
      has_sorts: response?.sorts?.length > 0,
      is_last_page: response?.last || false,
      page_number: page,
      page_size: pageSize,
      selected_sort: selectedSort,
      search_context: this.convertKeysToSnakeCase(searchQuery),
    });
  }

  trackBusSearchResultsError(
    error: any,
    searchQuery: any,
    loadTime: number,
    selectedSort: string
  ): void {
    this.trackUnified('results_loaded', {
      ...this.getBusFunnelContext('results', 2, 'error'),
      legacy_event_name: 'Bus Search Results Error',
      error_message: error?.message || 'Unknown error',
      load_time_ms: loadTime,
      search_context: {
        ...this.convertKeysToSnakeCase(searchQuery),
        sort_option: selectedSort || 'relevance',
      },
    });
  }

  trackBusSearchResultsPagination(
    action: string,
    currentCount: number,
    searchQuery: any,
    selectedSort: string
  ): void {
    this.trackUnified('results_pagination_triggered', {
      ...this.getBusFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Bus Search Results Pagination',
      action: action,
      current_page_size: currentCount,
      search_context: {
        ...this.convertKeysToSnakeCase(searchQuery),
        sort_option: selectedSort || 'relevance',
      },
    });
  }

  trackBusSearchResultsPaginationEmpty(totalResults: number): void {
    this.trackUnified('results_pagination_loaded', {
      ...this.getBusFunnelContext('results', 2, 'error'),
      legacy_event_name: 'Bus Search Results Pagination Empty',
      action: 'no_more_results',
      total_results: totalResults,
    });
  }

  trackBusFilter(action: string, selectedFilters): void {
    this.trackUnified('results_filter_changed', {
      ...this.getBusFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Bus Filter Applied',
      action: action,
      filter_context: {
        ...this.convertKeysToSnakeCase(selectedFilters),
      },
    });
  }

  trackBusSort(action: string, selectedSort: string): void {
    this.trackUnified('results_sort_changed', {
      ...this.getBusFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Bus Sort Applied',
      action: action,
      component: 'bus-sort-modal',
      button: 'apply-sort-button',
      selected_sort: selectedSort,
    });
  }

  trackBusSearchResultsEmpty(searchQuery: any): void {
    this.trackUnified('results_loaded', {
      ...this.getBusFunnelContext('results', 2, 'error'),
      legacy_event_name: 'Bus Search Results Empty',
      error_code: 'no_results',
      search_context: {
        ...this.convertKeysToSnakeCase(searchQuery),
      },
    });
  }

  trackBusProceedToReviewEvent(
    reviewId: string,
    route: any,
    totalFare: any
  ): void {
    this.trackUnified('checkout_started', {
      ...this.getBusFunnelContext('review', 4, 'success'),
      legacy_event_name: 'Bus Proceed To Review',
      review_id: reviewId,
      route: this.convertKeysToSnakeCase(route || {}),
      total_fare: this.convertKeysToSnakeCase(totalFare || {}),
    });
  }

  trackCreateBusBookingEvent(data: any): void {
    this.trackUnified('booking_created', {
      ...this.getBusFunnelContext('payment', 5, 'success'),
      legacy_event_name: 'Bus Create Booking',
      ...this.convertKeysToSnakeCase(data),
    });
  }

  trackBusBookingCompleted(booking: {
    bookingId: string;
    totalAmount?: number;
    currency?: string;
    seatsCount?: number;
  }): void {
    this.trackUnified('booking_completed', {
      ...this.getBusFunnelContext('confirmation', 6, 'success'),
      legacy_event_name: 'Bus Booking Completed',
      booking_id: booking?.bookingId,
      total_amount: booking?.totalAmount,
      currency: booking?.currency || 'INR',
      seats_count: booking?.seatsCount,
    });
  }

  trackBusSeatToggled(seat: any, selected: boolean): void {
    this.trackUnified('seat_toggled', {
      ...this.getBusFunnelContext('details', 3, 'na'),
      legacy_event_name: 'Bus Seat Toggled',
      seat_id: seat?.SeatNo || seat?.seatNo || seat?.id,
      selected: Boolean(selected),
      price: Number(
        seat?.priceDetail?.finalPrice ??
          seat?.PriceDetail?.finalPrice ??
          seat?.SeatRate ??
          0
      ),
      currency:
        seat?.priceDetail?.currency || seat?.PriceDetail?.currency || 'INR',
      deck: seat?.deck || null,
      seat_type: typeof seat?.seatType !== 'undefined' ? seat?.seatType : null,
    });
  }

  trackBusSearchResultsCardSelectEvent(busData: any): void {
    const priceDetail =
      busData?.priceDetail ||
      busData?.data?.roomDetail?.priceDetail ||
      busData?.Fare?.priceDetail ||
      {};

    this.trackUnified('item_selected', {
      ...this.getBusFunnelContext('details', 3, 'success'),
      legacy_event_name: 'Bus Selected',
      operator_name:
        busData?.routeDetails?.providerName || busData?.operatorName,
      bus_type:
        busData?.seatTypeDescription ||
        busData?.BusTypeName ||
        busData?.busType ||
        '',
      departure_time:
        busData?.routeDetails?.departureTime || busData?.departureTime || '',
      arrival_time:
        busData?.routeDetails?.arrivalTime || busData?.arrivalTime || '',
      available_seats:
        Number(busData?.availableSeats ?? busData?.routeDetails?.emptySeats) ||
        null,
      fare: {
        final_price: priceDetail?.finalPrice,
        currency: priceDetail?.currency,
        tax: priceDetail?.tax,
        total_price: priceDetail?.totalPrice,
        total_discount: priceDetail?.totalDiscount,
      },
      route: {
        source: busData?.routeDetails?.source || '',
        destination: busData?.routeDetails?.destination || '',
      },
      result_id: busData?.resultId || busData?.id || '',
    });
  }

  trackBusSearchResultsFilterOpened(
    searchQuery: any,
    filters: any[],
    busList: any[]
  ): void {
    this.trackUnified('results_filter_opened', {
      ...this.getBusFunnelContext('results', 2, 'na'),
      legacy_event_name: 'Bus Search Results Filter Opened',
      button: 'filter-button',
      current_filters: searchQuery?.fq || {},
      available_filters: (filters || []).map((f) => f?.name).filter(Boolean),
      total_buses: Array.isArray(busList) ? busList.length : 0,
      search_context: {
        source: searchQuery?.source || '',
        destination: searchQuery?.destination || '',
        departure_date: searchQuery?.departureDate || '',
      },
    });
  }

  trackBusSearchResultsPaginationSuccess(
    newResultsCount: number,
    totalResults: number,
    isLastPage: boolean
  ): void {
    this.trackUnified('results_pagination_loaded', {
      ...this.getBusFunnelContext('results', 2, 'success'),
      legacy_event_name: 'Bus Search Results Pagination Success',
      action: 'loaded_more_results',
      new_results_count: Number(newResultsCount) || 0,
      total_results: Number(totalResults) || 0,
      is_last_page: Boolean(isLastPage),
    });
  }

  // Manage consent
  setTrackingConsent(hasConsent: boolean): void {
    if (hasConsent) {
      this.mixpanel.opt_in_tracking();
    } else {
      this.mixpanel.opt_out_tracking();
    }
  }

  // Clear user data
  clearUserData(): void {
    this.mixpanel.reset();
    localStorage.removeItem('mixpanel_consent');
  }
}
